import { useState, useEffect } from 'react';

export interface BrandingConfig {
  productName: string;
  productTagline: string;
  companyName: string;
  logos: {
    primary: string;
    icon: string;
    white: string;
    dark: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  contact: {
    support: string;
    general: string;
    website: string;
    phone: string;
  };
  social: {
    linkedin: string;
    twitter: string;
  };
}

const defaultBranding: BrandingConfig = {
  productName: 'TalentKey.AI',
  productTagline: 'AI-Powered Talent Acquisition Platform',
  companyName: 'TalentKey.AI Inc.',
  logos: {
    primary: '/assets/logos/talentkey-logo.svg',
    icon: '/assets/logos/talentkey-icon.svg',
    white: '/assets/logos/talentkey-logo-white.svg',
    dark: '/assets/logos/talentkey-logo-dark.svg',
  },
  colors: {
    primary: '#2563eb',
    secondary: '#64748b',
    accent: '#10b981',
  },
  contact: {
    support: 'support@talentkey.ai',
    general: 'hello@talentkey.ai',
    website: 'https://talentkey.ai',
    phone: '+1 (555) 123-4567',
  },
  social: {
    linkedin: 'https://linkedin.com/company/talentkey-ai',
    twitter: 'https://twitter.com/talentkey_ai',
  },
};

export const useBranding = () => {
  const [branding, setBranding] = useState<BrandingConfig>(defaultBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch('/api/v1/branding/config');
        
        if (response.ok) {
          const data = await response.json();
          setBranding(data);
        } else {
          console.warn('Failed to fetch branding config, using defaults');
        }
      } catch (err) {
        console.warn('Error fetching branding config, using defaults:', err);
        setError('Failed to load branding configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  return {
    branding,
    loading,
    error,
    // Convenience getters
    productName: branding.productName,
    logo: branding.logos.primary,
    logoIcon: branding.logos.icon,
    logoWhite: branding.logos.white,
    logoDark: branding.logos.dark,
    primaryColor: branding.colors.primary,
    secondaryColor: branding.colors.secondary,
    accentColor: branding.colors.accent,
  };
}; 